import { useState, type ReactElement } from 'react';
import PublicIcon from '@mui/icons-material/Public';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import useHeaderTitle from 'app/hooks/useHeaderTitle';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import ErrorState from 'app/shared-components/common/ErrorState';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';

import usePlusWebsites from '../../../../hooks/usePlusWebsites';
import { websiteDetailsPath } from '../../../../paths';
import { useGetPages } from '../../hooks/useGetPages';
import { useGetPageDetails, type PageSeo } from '../hooks/useGetPageDetails';
import { useUpdatePageSeo } from '../hooks/useUpdatePageSeo';
import { useGetWebsiteTheme } from '../../../ThemeAndStyles/hooks/useGetWebsiteTheme';
import { getPrimaryFavicon } from '../../../ThemeAndStyles/favicons';
import { SeoFields } from './SeoFields';

interface PreviewProps {
  host: string;
  title?: string;
  description?: string;
  faviconUrl?: string;
}

function FaviconOrPlaceholder({ faviconUrl }: { faviconUrl?: string }): ReactElement {
  if (faviconUrl) {
    return (
      <img src={faviconUrl} alt="Favicon" className="vtwo:h-full vtwo:w-full vtwo:object-contain" />
    );
  }
  return <PublicIcon fontSize="small" className="vtwo:text-gray-500" />;
}

function SearchResultPreview({ host, title, description, faviconUrl }: PreviewProps): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      <Typography variant="body2" className="vtwo:text-typography-primary">
        This is how your key elements will look like:
      </Typography>
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
        <Box className="vtwo:flex vtwo:h-7 vtwo:w-7 vtwo:items-center vtwo:justify-center vtwo:overflow-hidden vtwo:rounded-full vtwo:bg-gray-100">
          <FaviconOrPlaceholder faviconUrl={faviconUrl} />
        </Box>
        <Box>
          <Typography variant="body2" className="vtwo:font-medium vtwo:text-typography-primary">
            {host || 'Website'}
          </Typography>
          {host && (
            <Typography variant="caption" className="vtwo:text-gray-500">
              {`https://${host}`}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography className="vtwo:text-xl vtwo:text-[#6E4FF1]">
        {title || 'Untitled page'}
      </Typography>
      <Typography variant="body2" className="vtwo:text-gray-600">
        {description || 'No description set.'}
      </Typography>
    </Box>
  );
}

interface SocialPreviewProps extends PreviewProps {
  imageUrl?: string;
}

function SocialPreview({ host, title, description, imageUrl }: SocialPreviewProps): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      <Typography variant="body2" className="vtwo:text-typography-primary">
        This is how your key elements will look like:
      </Typography>
      <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2 vtwo:rounded-lg vtwo:border vtwo:border-plus-border vtwo:bg-gray-50 vtwo:p-4">
        <Typography className="vtwo:font-bold vtwo:text-typography-primary">
          {title || 'Untitled page'}
        </Typography>
        {host && (
          <Typography variant="body2" className="vtwo:text-[#2f6fed]">
            {host}
          </Typography>
        )}
        <Typography variant="body2" className="vtwo:text-gray-600">
          {description || 'No description set.'}
        </Typography>
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt="Social preview"
            className="vtwo:mt-2 vtwo:max-h-80 vtwo:w-full vtwo:rounded-md vtwo:object-cover"
          />
        )}
      </Box>
    </Box>
  );
}

interface SeoEditFormProps {
  value: PageSeo;
  organisationId: string;
  isSaving: boolean;
  onChange: (field: keyof PageSeo, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

function SeoEditForm({
  value,
  organisationId,
  isSaving,
  onChange,
  onSave,
  onCancel,
}: SeoEditFormProps): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-4">
      <SeoFields value={value} organisationId={organisationId} onChange={onChange} />
      <Box className="vtwo:flex vtwo:justify-end vtwo:gap-3">
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isSaving}
          className="vtwo:rounded vtwo:text-typography-primary vtwo:border-plus-border"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving}
          className="vtwo:rounded vtwo:bg-[#327D5D] vtwo:text-white vtwo:hover:bg-[#28644A]"
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}

const SeoSettingsPage = (): ReactElement => {
  const location = useLocation();
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const organisationId = searchParams.get('organisationId') ?? '';

  // The name arrives in the navigation state from the pages list, but a reload has none —
  // so the cached pages list is the real source.
  const { pages } = useGetPages({ websiteId });
  const pageNameFromState = (location.state as { pageName?: string } | null)?.pageName;
  const pageName = pages.find((page) => page.id === pageId)?.name ?? pageNameFromState ?? 'Page';

  useNavigationHeader({
    title: 'Back',
    navigateTo: websiteDetailsPath(searchParams),
    backToHistory: false,
    replace: true,
  });
  useHeaderTitle(`SEO settings - ${pageName}`);

  const { plusWebsites } = usePlusWebsites();
  const website = plusWebsites.find((site) => site.id === websiteId) ?? null;
  const host = (website?.domain ?? website?.alternativeDomain ?? '').replace(/^https?:\/\//, '');
  const { theme } = useGetWebsiteTheme({ websiteId });
  const faviconUrl = getPrimaryFavicon(theme?.favicons ?? [])?.url;

  const { seo, isLoading, isError, error } = useGetPageDetails({
    websiteId,
    pageId: pageId ?? '',
  });

  const updateSeo = useUpdatePageSeo({ websiteId, pageId: pageId ?? '' });
  const [draft, setDraft] = useState<PageSeo | null>(null);
  const isEditing = draft !== null;

  const startEditing = () =>
    setDraft({
      title: seo.title ?? '',
      description: seo.description ?? '',
      socialTitle: seo.socialTitle ?? '',
      socialDescription: seo.socialDescription ?? '',
      socialImageUrl: seo.socialImageUrl ?? '',
    });

  const handleFieldChange = (field: keyof PageSeo, value: string) =>
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));

  const handleSave = () => {
    if (!draft) return;
    // socialImageUrl is validated as a URL whenever the key is present, so a page without a social
    // image has to leave it out entirely — "" is not a URL and the whole save is rejected. The text
    // fields keep their empty strings: that is how this PUT is told to clear them.
    const { socialImageUrl, ...withoutImage } = draft;
    updateSeo.mutate(socialImageUrl?.trim() ? draft : withoutImage, {
      onSuccess: () => setDraft(null),
    });
  };

  if (isError) {
    return (
      <ErrorState
        title="Failed to load SEO settings"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  return (
    <MarketingStationLayout className="vtwo:h-full vtwo:overflow-y-auto vtwo:p-6">
      <Box className="vtwo:mx-auto vtwo:flex vtwo:max-w-3xl vtwo:flex-col vtwo:gap-6 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white vtwo:p-8">
        <Box className="vtwo:flex vtwo:items-center vtwo:justify-between">
          <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary">
            SEO settings
          </Typography>
          {!isLoading && !isEditing && (
            <Button
              variant="outlined"
              onClick={startEditing}
              className="vtwo:rounded vtwo:text-featured vtwo:border-featured vtwo:hover:border-featured-hover vtwo:hover:bg-featured/[0.04]"
            >
              Edit
            </Button>
          )}
        </Box>

        <Divider />

        {isLoading && (
          <Box className="vtwo:flex vtwo:justify-center vtwo:py-8">
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && isEditing && draft && (
          <SeoEditForm
            value={draft}
            organisationId={organisationId}
            isSaving={updateSeo.isLoading}
            onChange={handleFieldChange}
            onSave={handleSave}
            onCancel={() => setDraft(null)}
          />
        )}

        {!isLoading && !isEditing && (
          <>
            <SearchResultPreview
              host={host}
              title={seo.title}
              description={seo.description}
              faviconUrl={faviconUrl}
            />
            <Divider />
            <SocialPreview
              host={host}
              title={seo.socialTitle}
              description={seo.socialDescription}
              imageUrl={seo.socialImageUrl}
            />
          </>
        )}
      </Box>
    </MarketingStationLayout>
  );
};

export default SeoSettingsPage;
